// Translates the registration payload returned by GET /clients/{id}/registration
// (snake_case DB columns + nested JSONB) into the camelCase formData shape that
// generateFormPdf and the resume-mode form pages expect. Keeping this in one
// place means PDF download / resume edit / view-all-forms all read the same
// fields the original forms wrote.

const concatName = (first, last) => [first, last].filter(Boolean).join(' ').trim();

const splitAddress = (line1, line2) => [line1, line2].filter(Boolean).join(', ');

// inquiry_data (operational.client_inquiry row) → enquiryForm formData
export function inquiryToEnquiryForm(inquiry) {
  if (!inquiry) return null;
  const childName = concatName(inquiry.child_first_name, inquiry.child_last_name);
  const fatherName = concatName(inquiry.parent_first_name, inquiry.parent_last_name);
  const services = inquiry.preferred_services || {};

  return {
    childName,
    dateOfBirth: inquiry.child_date_of_birth || '',
    gender: inquiry.child_gender || '',
    fatherName: fatherName || '',
    fatherMobile: inquiry.parent_phone || '',
    fatherEmail: inquiry.parent_email || '',
    motherMobile: inquiry.parent_alternate_phone || '',
    streetAddress: splitAddress(inquiry.address_line1, inquiry.address_line2),
    city: inquiry.city || '',
    state: inquiry.state || '',
    zipCode: inquiry.postal_code || '',
    source: inquiry.source_code || '',
    sourceDetails: inquiry.source_detail || '',
    primaryConcern: inquiry.primary_diagnosis || '',
    lookingFor: services.type || '',
    subOptions: services.sub || '',
    // The centre the enquiry was FILED AGAINST, not the centre of whoever is
    // opening it. Without this a reopened enquiry would silently re-default to
    // the reader's own centre and could be saved back under the wrong one.
    centerId: inquiry.center_id || '',
    centerName: inquiry.center_name || '',
    comments: inquiry.notes || '',
    enquiryDate: inquiry.inquiry_date || ''
  };
}

// intake_data (operational.client_intake row, JSONB columns) → enquiryForm formData
//
// THE THIRD SOURCE for the enquiry, and for some children the only one.
//
// A child registered while Enquiry and Intake were one form has NO inquiry row and
// no verbatim blob - the merged flow wrote a single intake. Its enquiry answers are
// still all there, in the intake's own structured columns: child_demographics
// carries the child and address, parent_demographics both parents, and
// child_demographics.enquiry the referral questions. Reading only inquiry_data left
// those children with no enquiry data at all, which is why the Onboarding tab
// offered Download PDF on their Intake row and not their Enquiry row.
//
// Blank keys are omitted rather than emitted as '', so this can be layered UNDER a
// real blob without a blank here overwriting a real answer there.
export function intakeToEnquiryForm(intake) {
  if (!intake || typeof intake !== 'object') return null;
  const cd = intake.child_demographics || {};
  const pd = intake.parent_demographics || {};
  const enq = cd.enquiry || {};
  const services = intake.recommended_services || {};

  const out = {};
  const put = (key, value) => {
    if (value !== undefined && value !== null && value !== '') out[key] = value;
  };

  put('childName', cd.child_name);
  put('dateOfBirth', cd.date_of_birth);
  put('gender', cd.gender);
  put('languageAtHome', cd.language_at_home);
  put('currentSchoolProgram', cd.current_school_program);

  // street_address is the split field; `address` is the merged form's single box,
  // kept only as a fallback for a row written before the split.
  put('streetAddress', cd.street_address || cd.address);
  put('city', cd.city);
  put('state', cd.state);
  put('zipCode', cd.zip_code);

  put('fatherName', pd.father_name);
  put('fatherOccupation', pd.father_occupation);
  put('fatherPlaceOfWork', pd.father_place_of_work);
  put('fatherMobile', pd.father_mobile);
  put('fatherEmail', pd.father_email);
  put('motherName', pd.mother_name);
  put('motherOccupation', pd.mother_occupation);
  put('motherPlaceOfWork', pd.mother_place_of_work);
  put('motherMobile', pd.mother_mobile);
  put('motherEmail', pd.mother_email);

  // how_did_you_know is the retired free-text version of source; it only wins when
  // the structured code is absent.
  put('source', enq.source || enq.how_did_you_know);
  put('sourceDetails', enq.source_details);
  put('primaryConcern', enq.primary_concern);
  put('primaryConcernDetails', enq.primary_concern_details);
  put('consultedDoctor', enq.consulted_doctor);
  put('formalAssessments', enq.formal_assessments);
  put('comments', enq.comments);
  put('status', enq.status);
  put('enquiryDate', enq.enquiry_date);

  put('lookingFor', services.type);
  put('subOptions', services.sub);

  // The stored `age` is a single number the merged form kept, and the two forms
  // disagreed about its unit - "9.4" meant nine years four months on one of them.
  // Only a plain integer is safe to read as years; anything else is left for the
  // form to derive from the date of birth rather than guessed at.
  if (/^\d+$/.test(String(cd.age ?? '').trim())) put('ageYears', String(cd.age).trim());

  return Object.keys(out).length ? out : null;
}

// intake_data (operational.client_intake row, JSONB columns) → intakeForm formData
export function intakeToIntakeForm(intake) {
  if (!intake) return null;
  const cd = intake.child_demographics || {};
  const pd = intake.parent_demographics || {};
  const mh = intake.medical_history || {};
  const dh = intake.developmental_history || {};

  const fam = mh.family_history || {};
  const pre = mh.prenatal || {};
  const moH = mh.mothers_health || {};
  const lab = mh.labor_delivery || {};
  const bre = mh.breathing_problems || {};
  const speech = dh.speech_milestones || {};
  const motor = dh.motor_milestones || {};
  const sensory = dh.sensory_processing || {};
  // Remaining nested sections — these were previously dropped, so PDFs/edits
  // built from the structured fallback (no verbatim blob) lost Referral,
  // Secondary Concerns, Behaviours, Language, School, Academic, Motor,
  // Self-Help, Sleep, Community, Therapy Readiness and Goals.
  const enq = cd.enquiry || {};
  const referral = dh.referral || {};
  const secondary = dh.secondary_concerns || {};
  const beh = dh.behaviours || {};
  const behLoc = beh.location || {};
  const behAnt = beh.antecedents || {};
  const behCons = beh.consequences || {};
  const lc = dh.language_communication || {};
  const expr = dh.expressive_language || {};
  const sp = dh.speech_patterns || {};
  const rec = dh.receptive_language || {};
  const play = dh.play_skills || {};
  const school = dh.school_info || {};
  const acad = dh.academic_skills || {};
  const fineMotor = dh.fine_motor || {};
  const grossMotor = dh.gross_motor || {};
  const selfHelp = dh.self_help || {};
  const sleep = mh.sleep || {};
  const community = mh.community_support || {};
  const readiness = dh.therapy_readiness || {};
  const goals = dh.goals_of_therapy || {};

  return {
    // Child
    childName: cd.child_name || '',
    childId: cd.child_id || '',
    age: cd.age || '',
    dateOfBirth: cd.date_of_birth || '',
    gender: cd.gender || '',
    languageAtHome: cd.language_at_home || '',
    address: cd.address || '',
    currentSchoolProgram: cd.current_school_program || '',
    // Parents
    fatherName: pd.father_name || '',
    fatherOccupation: pd.father_occupation || '',
    fatherMobile: pd.father_mobile || '',
    fatherEmail: pd.father_email || '',
    motherName: pd.mother_name || '',
    motherOccupation: pd.mother_occupation || '',
    motherMobile: pd.mother_mobile || '',
    motherEmail: pd.mother_email || '',
    // Family history
    familyLanguageDisorder: fam.language_disorder || '',
    familyAnxiety: fam.anxiety || '',
    familyADHD: fam.adhd || '',
    familyASD: fam.asd || '',
    familyBipolar: fam.bipolar || '',
    familyDepression: fam.depression || '',
    familyEpilepsy: fam.epilepsy || '',
    familyGeneticCondition: fam.genetic_condition || '',
    familyGeneticDisorderDetail: fam.genetic_disorder_detail || '',
    familyHospitalizedEmotional: fam.hospitalized_emotional || '',
    familyIntellectualDisability: fam.intellectual_disability || '',
    familyMotorVocalTics: fam.motor_vocal_tics || '',
    familyLearningDisabilities: fam.learning_disabilities || '',
    familyDisorderDetails: fam.details || '',
    // Prenatal
    motherAgeAtChildbirth: pre.mother_age_at_childbirth || '',
    fatherAgeAtChildbirth: pre.father_age_at_childbirth || '',
    fullTimePregnancy: pre.full_time_pregnancy || '',
    weeksPriorOrAfterDue: pre.weeks_prior_or_after_due || '',
    pregnancyNumber: pre.pregnancy_number || '',
    multipleBirth: pre.multiple_birth || '',
    previousPregnancyProblems: pre.previous_pregnancy_problems || '',
    // Mother's health
    noHealthProblems: moH.no_health_problems || '',
    severNausea: moH.severe_nausea || '',
    highBloodPressure: moH.high_blood_pressure || '',
    healthNotKnown: moH.health_not_known || '',
    infection: moH.infection || '',
    seizuresDuringPregnancy: moH.seizures || '',
    eclampsia: moH.eclampsia || '',
    mothersHealthDetails: moH.details || '',
    // Labor & delivery
    noProblemDuringLabor: lab.no_problem || '',
    laborNotKnown: lab.not_known || '',
    excessiveBleeding: lab.excessive_bleeding || '',
    umbilicalCordAroundNeck: lab.umbilical_cord_around_neck || '',
    feverInfectionMother: lab.fever_infection_mother || '',
    breathingDifficultiesChild: lab.breathing_difficulties_child || '',
    placentaBrokeMoreThan1Day: lab.placenta_broke_more_than_1_day || '',
    childHealthyNewborn: lab.child_healthy_newborn || '',
    birthDefects: lab.birth_defects || '',
    newbornIntensiveCare: lab.newborn_intensive_care || '',
    howBabyWasBorn: lab.how_baby_was_born || '',
    laborDeliveryDetails: lab.details || '',
    childNeedSpecialCare: lab.child_need_special_care || '',
    laborRemarks: lab.remarks || '',
    // Breathing problems
    oxygen: bre.oxygen || '',
    ventilator: bre.ventilator || '',
    incubator: bre.incubator || '',
    bloodTransfusion: bre.blood_transfusion || '',
    muscleWeaknessParalysis: bre.muscle_weakness_paralysis || '',
    poorMuscleTone: bre.poor_muscle_tone || '',
    seizureBreathing: bre.seizure || '',
    feedingDifficulties: bre.feeding_difficulties || '',
    jaundiceLights: bre.jaundice_lights || '',
    infectionBreathing: bre.infection || '',
    excessiveSensitivityNoise: bre.excessive_sensitivity_noise || '',
    breathingProblemsDetails: bre.details || '',
    // Speech milestones
    interestInSoundBy3Months: speech.interest_in_sound_3m || '',
    babblingBy4to6Months: speech.babbling_4_6m || '',
    understandingWordsBy6to11Months: speech.understanding_words_6_11m || '',
    firstWordsBy12Months: speech.first_words_12m || '',
    shortPhrasesBy24Months: speech.short_phrases_24m || '',
    delaysMotorDevelopment: speech.delays_motor || '',
    bedWettingAfterTraining: speech.bed_wetting || '',
    // Motor milestones
    turnOverBy6Months: motor.turn_over_6m || '',
    sitAloneBy9to12Months: motor.sit_alone_9_12m || '',
    crawlBy9to12Months: motor.crawl_9_12m || '',
    standAloneBy9to12Months: motor.stand_alone_9_12m || '',
    walkAloneBy12to18Months: motor.walk_alone_12_18m || '',
    walkUpstairsBy36Months: motor.walk_upstairs_36m || '',
    walkDownstairsBy48Months: motor.walk_downstairs_48m || '',
    running: motor.running || '',
    // Sensory
    toleratingFoodTextures: sensory.tolerating_food_textures || '',
    gaggingOrVomiting: sensory.gagging_or_vomiting || '',
    toleratingClothing: sensory.tolerating_clothing || '',
    toleratingTouchFromOthers: sensory.tolerating_touch || '',
    doesNotNoticePain: sensory.does_not_notice_pain || '',
    sensoryNoneOfAbove: sensory.none_of_above || '',
    sensoryDetails: sensory.details || '',
    sensoryRemarks: sensory.remarks || '',
    // Developmental top-level
    delaysSocialDevelopment: dh.delays_social || '',
    delaysLanguageDevelopment: dh.delays_language || '',
    infantEnjoyCuddling: dh.infant_enjoy_cuddling || '',
    earlyLifeDescription: dh.early_life_description || '',
    // Enquiry (under child_demographics) + recommended services
    howDidYouKnow: enq.how_did_you_know || '',
    primaryConcern: enq.primary_concern || '',
    consultedDoctor: enq.consulted_doctor || '',
    formalAssessments: enq.formal_assessments || '',
    lookingFor: (intake.recommended_services && intake.recommended_services.type) || '',
    // Hearing / Sleep / Community Support / Medications
    hearing: mh.hearing || '',
    bedtime: sleep.bedtime || '',
    awake: sleep.awake || '',
    nap: sleep.nap || '',
    sleepPatternDetails: sleep.details || '',
    sleepRemarks: sleep.remarks || '',
    otServices: community.ot_services || '',
    stServices: community.st_services || '',
    otherCommunitySupport: community.other || '',
    communityRemarks: community.remarks || '',
    medications: Array.isArray(mh.medications) ? mh.medications : [],
    // Reason for Referral
    referralSpeechLanguage: referral.speech_language || '',
    referralSocialSkills: referral.social_skills || '',
    referralNameCallResponse: referral.name_call_response || '',
    referralEyeContact: referral.eye_contact || '',
    referralRegression: referral.regression || '',
    regressionSocialFunctioning: referral.regression_social_functioning || '',
    regressionSpeechLanguage: referral.regression_speech_language || '',
    regressionProblemSolving: referral.regression_problem_solving || '',
    regressionMotorCoordination: referral.regression_motor_coordination || '',
    regressionMedicalHistory: referral.regression_medical_history || '',
    otherReferralConcerns: referral.other_concerns || '',
    referralRemarks: referral.remarks || '',
    // Secondary Concerns
    aggression: secondary.aggression || '',
    selfInjuriousBehaviour: secondary.self_injurious_behaviour || '',
    disruptiveBehaviour: secondary.disruptive_behaviour || '',
    elopement: secondary.elopement || '',
    pica: secondary.pica || '',
    noncompliance: secondary.noncompliance || '',
    tantrums: secondary.tantrums || '',
    toileting: secondary.toileting || '',
    inappropriateSexualBehaviour: secondary.inappropriate_sexual_behaviour || '',
    inappropriateVocalizations: secondary.inappropriate_vocalizations || '',
    otherSecondaryConcern: secondary.other || '',
    secondaryConcernsDetails: secondary.details || '',
    // Behaviours — location / antecedents / consequences
    behaviorHome: behLoc.home || '',
    behaviorSchool: behLoc.school || '',
    behaviorPublic: behLoc.public || '',
    targetBehavior: behLoc.target_behavior || '',
    behaviorDetails: behLoc.details || '',
    antecedentDemands: behAnt.demands || '',
    antecedentTransitions: behAnt.transitions || '',
    antecedentDivertedAttention: behAnt.diverted_attention || '',
    antecedentRestrictedAccess: behAnt.restricted_access || '',
    antecedentRemovalOfItem: behAnt.removal_of_item || '',
    antecedentAllSituations: behAnt.all_situations || '',
    antecedentExamples: behAnt.examples || '',
    consequenceVerbalReprimand: behCons.verbal_reprimand || '',
    consequenceTimeOut: behCons.time_out || '',
    consequenceEscapeDemand: behCons.escape_demand || '',
    consequenceRemovalPreferred: behCons.removal_preferred || '',
    consequenceAccessPreferred: behCons.access_preferred || '',
    consequencePhysicalAttention: behCons.physical_attention || '',
    consequenceIgnore: behCons.ignore || '',
    consequenceRestraint: behCons.restraint || '',
    consequenceCorporalPunishment: behCons.corporal_punishment || '',
    consequenceYelling: behCons.yelling || '',
    consequenceOther: behCons.other || '',
    consequenceExplain: behCons.explain || '',
    consequenceRemarks: behCons.remarks || '',
    // Language & Communication
    nonVocal: lc.non_vocal || '',
    singleWords: lc.single_words || '',
    twoWordPhrases: lc.two_word_phrases || '',
    shortSentences: lc.short_sentences || '',
    reciprocalConversations: lc.reciprocal_conversations || '',
    leadingAdultsToItems: lc.leading_adults_to_items || '',
    signs: lc.signs || '',
    gesturesPointing: lc.gestures_pointing || '',
    pictureCommunication: lc.picture_communication || '',
    languageOther: lc.other || '',
    howChildCommunicates: expr.how_child_communicates || '',
    childImitatesSounds: expr.child_imitates_sounds || '',
    childLabelsItems: expr.child_labels_items || '',
    vocalizationsClearOrApprox: expr.vocalizations_clear_or_approx || '',
    strategiesToIncrease: expr.strategies_to_increase || '',
    immediateEcholalia: sp.immediate_echolalia || '',
    delayedEcholalia: sp.delayed_echolalia || '',
    pronounReversal: sp.pronoun_reversal || '',
    oddLoudTone: sp.odd_loud_tone || '',
    understandLanguage: rec.understand_language || '',
    followInstructions: rec.follow_instructions || '',
    identifyObjects: rec.identify_objects || '',
    // Play
    playWithSiblings: play.with_siblings || '',
    playWithOtherChildren: play.with_other_children || '',
    playWithAdults: play.with_adults || '',
    playRemarks: play.remarks || '',
    // School & Academic
    schoolName: school.school_name || '',
    grade: school.grade || '',
    classroomSetup: school.classroom_setup || '',
    educationalProgramFocus: school.educational_program_focus || '',
    classroomType: school.classroom_type || '',
    schoolPrimaryConcern: Array.isArray(school.primary_concern) ? school.primary_concern : [],
    recreationalHobbies: school.recreational_hobbies || '',
    readingLetter: acad.reading_letter || '',
    readingWord: acad.reading_word || '',
    readingSentence: acad.reading_sentence || '',
    readingParagraph: acad.reading_paragraph || '',
    writingTripodGrip: acad.writing_tripod_grip || '',
    writingTracing: acad.writing_tracing || '',
    writingAlphabets: acad.writing_alphabets || '',
    writingWords: acad.writing_words || '',
    writingSentences: acad.writing_sentences || '',
    writingParagraph: acad.writing_paragraph || '',
    writingOther: acad.writing_other || '',
    mathNumbers: acad.math_numbers || '',
    mathFourOperations: acad.math_four_operations || '',
    mathComprehension: acad.math_comprehension || '',
    academicRemarks: acad.remarks || '',
    // Fine / Gross Motor + Self-Help
    usingUtensils: fineMotor.using_utensils || '',
    turnPagesOfBook: fineMotor.turn_pages_of_book || '',
    foldingPapers: fineMotor.folding_papers || '',
    holdingCrayonPencil: fineMotor.holding_crayon_pencil || '',
    tyingShoelaces: fineMotor.tying_shoelaces || '',
    otherFineMotor: fineMotor.other || '',
    walking: grossMotor.walking || '',
    jumping: grossMotor.jumping || '',
    skipping: grossMotor.skipping || '',
    rollingABall: grossMotor.rolling_a_ball || '',
    ridingABike: grossMotor.riding_a_bike || '',
    otherGrossMotor: grossMotor.other || '',
    toiletTrained: selfHelp.toilet_trained || '',
    pottyTrained: selfHelp.potty_trained || '',
    brushesTeeth: selfHelp.brushes_teeth || '',
    bathesSelf: selfHelp.bathes_self || '',
    dressesSelf: selfHelp.dresses_self || '',
    feedsSelf: selfHelp.feeds_self || '',
    otherSelfHelp: selfHelp.other || '',
    toiletingDetails: selfHelp.toileting_details || '',
    selfHelpRemarks: selfHelp.remarks || '',
    // Therapy Readiness + Goals of Therapy
    previousABAServices: readiness.previous_aba_services || '',
    scheduleAvailabilityDate: readiness.schedule_availability_date || '',
    therapyReadinessComments: readiness.comments || '',
    goalDiagnosis: goals.diagnosis || '',
    goalTreatmentRecommendations: goals.treatment_recommendations || '',
    goalResourcesReferrals: goals.resources_referrals || '',
    goalOther: goals.other || '',
    recommendations: goals.recommendations || '',
    goalsRemarks: goals.remarks || ''
  };
}

// Stored IEP data is already in formData camelCase shape (saved by iep.jsx via
// PUT /clients/{id}/registration/iep). Pass through, returning null on empty.
export function iepDataToIepForm(iepData) {
  if (!iepData || typeof iepData !== 'object') return null;
  return iepData;
}

// Pick the right transformer for a given form id. The verbatim form blobs
// (enquiry_form_data / intake_form_data) win when present — the structured
// tables drop fields the form actually collects (mother's email, occupations,
// language at home, etc.). The translated row is the fallback for older
// clients registered before we started saving the blob.
export function registrationDataForForm(formId, registration) {
  if (!registration) return null;
  switch (formId) {
    // The merged form. Retired as a UI concept, still answered for anything
    // holding the old id. The API keeps reconstructing `enquiry_intake_form_data`
    // from the two blobs, so this only falls back when there is no verbatim blob
    // at all — a child written by the workflow flow, which populates the
    // structured tables only.
    case 'enquiryIntakeForm':
      return (
        registration.enquiry_intake_form_data || {
          ...intakeToIntakeForm(registration.intake_data),
          ...inquiryToEnquiryForm(registration.inquiry_data)
        }
      );
    // Three sources, best first. The last covers a child registered while the two
    // forms were one: no inquiry row, no blob, everything in the intake's columns.
    case 'enquiryForm':
      return (
        registration.enquiry_form_data || inquiryToEnquiryForm(registration.inquiry_data) || intakeToEnquiryForm(registration.intake_data)
      );
    // The Intake PDF prints the child's name and both parents' contact details in
    // its header sections. Those fields are owned by the Enquiry and the API
    // strips them from the intake blob, so they are layered back UNDER it here.
    //
    // This is deliberately not what the intake FORM does. A form that re-asks for
    // an answer invites a second, conflicting one; a printed clinical record with
    // no child's name on it is not usable at all. The enquiry values sit
    // underneath, so anything the intake genuinely holds still wins.
    case 'intakeForm': {
      const identity =
        registration.enquiry_form_data ||
        inquiryToEnquiryForm(registration.inquiry_data) ||
        intakeToEnquiryForm(registration.intake_data) ||
        {};
      return { ...identity, ...(registration.intake_form_data || intakeToIntakeForm(registration.intake_data)) };
    }
    case 'iep':
      return iepDataToIepForm(registration.iep_data);
    default:
      return null;
  }
}
